import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles, Square, ThumbsUp, ThumbsDown, ChevronDown, Lightbulb, Zap, Brain, Mic, MicOff,
  EyeOff, Eye, FileText, Share2, Archive,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChatIaCuerpo } from "@/components/admin/ChatIaPanel";
import { ChatIaHistorial } from "@/components/admin/ChatIaHistorial";
import { AccionMensaje, AccionesMensaje, BotonCopiar, ChatIaAviso, ChatIaMensajes, EsqueletoHilo } from "@/components/admin/ChatIaMensajes";
import { ChatIaComposer } from "@/components/admin/ChatIaComposer";
import { ChatIaBienvenida } from "@/components/admin/ChatIaBienvenida";
import {
  useMensajes, useCalificarMensaje, useResolverMemoria, usePuedeAdministrarAgente,
  useMensajesOcultos, useOcultarMensaje, useConversaciones, useActualizarConversacion, leerMensajesCeo,
  type Conversacion, type MensajeGuardado,
} from "@/hooks/usePortalAltaDireccion/useAgenteCeo";
import { useDictado } from "@/hooks/usePortalAltaDireccion/useDictado";
import {
  useChatAgenteCeo, type ContextoPantalla, type ModoAgente, type PropuestaMemoria,
} from "@/hooks/usePortalAltaDireccion/useChatAgenteCeo";
import { primeraPregunta, separarHistorial, type ConversacionIa } from "@/lib/chatIa";

/**
 * Conversación e historial del Agente CEO sobre el chat global del admin (`ChatIaPanel`,
 * `ChatIaHistorial`, `ChatIaMensajes`, `ChatIaComposer`, `ChatIaBienvenida`): la misma UX que la
 * Guía del portal y el Analista de tickets. Sirven al drawer y a la página completa (spec 6.1 y
 * 6.2): cambia el envoltorio, no la conversación.
 *
 * Lo propio del CEO entra por los slots: párrafos con cifras y citas de memoria (M-###), tarjeta
 * del brief, propuestas de memoria, pie con fuentes, calificación y "Quitar del contexto", modo
 * Rápido/Profundo, Detener, dictado, conversaciones compartidas con Dirección y archivadas.
 *
 * El texto en vivo llega por SSE y se pinta con cursor; lo ya guardado se lee de `ia_mensajes`.
 * Cuando un turno termina, la fuente vuelve a ser la base de datos: así lo que se ve al recargar
 * es exactamente lo que quedó registrado.
 */

/** Marcador interno del turno del brief. No se muestra nunca: no lo escribió nadie. */
export const MARCA_BRIEF = "(brief de apertura)";

const SUGERIDAS = [
  "¿Cómo va el servicio a clientes vs el mes pasado?",
  "¿Qué tickets de clientes están en riesgo hoy?",
  "¿Dónde se atora el tiempo de atención?",
  "¿Qué 3 cosas debería atender esta semana?",
];

type ChatCeo = ReturnType<typeof useChatAgenteCeo>;

/** Resalta cifras y negritas: los números son el argumento, tienen que saltar. */
const NUM = /\d[\d.,]*\s?%|\$\s?[\d.,]+|\b\d[\d.,]*\s?(?:días|día|horas|hora|tickets|ticket|MXN)\b/i;
function conNumeros(texto: string, k: string) {
  return texto.split(new RegExp(`(${NUM.source})`, "gi")).map((p, i) =>
    new RegExp(`^(?:${NUM.source})$`, "i").test(p)
      ? <span key={`${k}n${i}`} className="font-semibold tabular-nums text-foreground">{p}</span>
      : <span key={`${k}t${i}`}>{p}</span>);
}
function conFormato(texto: string, k: string) {
  return texto.split(/(\*\*[^*]+\*\*|M-\d+)/g).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={`${k}b${i}`} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
    }
    // M-### es la cita de una memoria: se marca para que se vea de dónde salió la regla.
    if (/^M-\d+$/.test(p)) {
      return (
        <Tooltip key={`${k}m${i}`}>
          <TooltipTrigger asChild>
            <span className="cursor-default rounded bg-primary/10 px-1 text-[11px] font-medium text-primary">{p}</span>
          </TooltipTrigger>
          <TooltipContent className="text-xs">Regla aprobada que el agente aplicó</TooltipContent>
        </Tooltip>
      );
    }
    return <span key={`${k}s${i}`}>{conNumeros(p, `${k}-${i}`)}</span>;
  });
}

function Parrafos({ texto, k }: { texto: string; k: string }) {
  const bloques = texto.split("\n").filter((l) => l.trim().length > 0);
  return (
    <div className="space-y-1.5 text-[13.5px] leading-relaxed text-foreground/90">
      {bloques.map((linea, i) => {
        const vineta = /^\s*[-*•]\s+/.test(linea);
        const numerada = /^\s*\d+[.)]\s+/.test(linea);
        return (
          <p key={`${k}p${i}`} className={cn(vineta || numerada ? "pl-4 -indent-4" : "")}>
            {conFormato(linea, `${k}-${i}`)}
          </p>
        );
      })}
    </div>
  );
}

/** Tarjeta ámbar de propuesta de memoria (spec 6.2). */
function TarjetaPropuesta({ propuesta }: { propuesta: PropuestaMemoria }) {
  const { data: puedeAdministrar } = usePuedeAdministrarAgente();
  const resolver = useResolverMemoria();
  const [resuelta, setResuelta] = useState<string | null>(null);
  const esPersonal = propuesta.alcance === "usuario";

  if (resuelta) {
    return (
      <div className="mt-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        {resuelta}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
        {esPersonal ? "Guardado para ti" : "Propuesta para la memoria del agente"}
      </p>
      <p className="mt-1.5 text-[13px] text-foreground/90">{propuesta.contenido}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {esPersonal
          ? "Es una preferencia tuya: se aplica ya y solo a ti."
          : "Queda pendiente de aprobación en Configuración › Memoria."}
      </p>
      {(esPersonal || puedeAdministrar) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {esPersonal ? (
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => resolver.mutate(
                { id: propuesta.id, decision: "archivar" },
                { onSuccess: () => setResuelta("Se deshizo la preferencia.") },
              )}
            >
              Deshacer
            </Button>
          ) : (
            <>
              <Button
                size="sm" className="h-7 text-xs"
                onClick={() => resolver.mutate(
                  { id: propuesta.id, decision: "aprobar" },
                  {
                    onSuccess: () => setResuelta("Aprobada: el agente la aplicará desde la siguiente pregunta."),
                    onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo aprobar"),
                  },
                )}
              >
                Aprobar ahora
              </Button>
              <Button
                size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => resolver.mutate(
                  { id: propuesta.id, decision: "rechazar", motivo: "Descartada desde la conversación" },
                  { onSuccess: () => setResuelta("Descartada.") },
                )}
              >
                Descartar
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const MOTIVOS: Array<{ id: string; etiqueta: string }> = [
  { id: "dato_incorrecto", etiqueta: "Dato incorrecto" },
  { id: "generico", etiqueta: "Genérico" },
  { id: "no_respondio", etiqueta: "No respondió" },
  { id: "muy_largo", etiqueta: "Muy largo" },
  { id: "falta_contexto", etiqueta: "Le faltó contexto" },
];

/** Pie de una respuesta: de dónde salió, qué opinas de ella, copiar y quitar del contexto. */
function PieRespuesta({
  idMensaje, texto, herramientas, oculto, onOcultar,
}: {
  idMensaje: string | null;
  texto: string;
  herramientas: Array<{ nombre: string; argumentos?: unknown; ms?: number; error?: string | null; truncado?: boolean }>;
  oculto?: boolean;
  onOcultar?: () => void;
}) {
  const calificar = useCalificarMensaje();
  const [verFuentes, setVerFuentes] = useState(false);
  const [voto, setVoto] = useState<1 | -1 | null>(null);
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);

  return (
    <div className="w-full">
      {herramientas.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setVerFuentes((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className={cn("h-3 w-3 transition-transform", verFuentes && "rotate-180")} />
            Fuentes ({herramientas.length})
          </button>
          {verFuentes && (
            <ul className="mt-1.5 space-y-1">
              {herramientas.map((h, i) => (
                <li key={`${h.nombre}${i}`} className="rounded border border-border bg-muted/30 px-2 py-1.5 text-[11px]">
                  <span className="font-medium text-foreground">{h.nombre}</span>
                  {h.error && <span className="ml-1.5 text-destructive">· {h.error}</span>}
                  {h.truncado && (
                    <span className="ml-1.5 text-amber-600">· resumido, no vio todo el detalle</span>
                  )}
                  {typeof h.ms === "number" && <span className="ml-1.5 text-muted-foreground">· {h.ms} ms</span>}
                  {!!h.argumentos && Object.keys(h.argumentos as object).length > 0 && (
                    <div className="mt-0.5 break-all font-mono text-[10px] text-muted-foreground">
                      {JSON.stringify(h.argumentos)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <AccionesMensaje className="-ml-2 mt-1">
        <AccionMensaje
          etiqueta="Útil"
          activo={voto === 1}
          disabled={!idMensaje}
          onClick={() => { setVoto(1); if (idMensaje) calificar.mutate({ idMensaje, valor: 1 }); }}
        >
          <ThumbsUp className={cn(voto === 1 && "text-emerald-600")} />
        </AccionMensaje>
        <AccionMensaje
          etiqueta="No sirvió"
          activo={voto === -1}
          disabled={!idMensaje}
          onClick={() => { setVoto(-1); setPidiendoMotivo(true); }}
        >
          <ThumbsDown className={cn(voto === -1 && "text-destructive")} />
        </AccionMensaje>
        <BotonCopiar texto={texto} />
        {onOcultar && (
          <AccionMensaje
            etiqueta={oculto ? "Devolver al contexto" : "Quitar del contexto de esta conversación"}
            onClick={onOcultar}
          >
            {oculto ? <Eye /> : <EyeOff />}
            {oculto ? "Devolver" : "Quitar del contexto"}
          </AccionMensaje>
        )}
      </AccionesMensaje>

      {pidiendoMotivo && idMensaje && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {MOTIVOS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                calificar.mutate({ idMensaje, valor: -1, motivo: m.id });
                setPidiendoMotivo(false);
                toast.success("Gracias: eso ayuda a afinar al agente");
              }}
              className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {m.etiqueta}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Un mensaje del hilo del CEO: guardado (de `ia_mensajes`) o el turno en vivo. */
interface MensajeCeo {
  id: string;
  rol: "usuario" | "asistente";
  texto: string;
  atenuado?: boolean;
  /** Respuesta al brief: se pinta con su tarjeta y la hora en que se armó. */
  brief?: string | null;
  guardado?: MensajeGuardado;
  enVivo?: boolean;
}

/** Conversación del CEO en el historial global, con la fila original para sus acciones. */
type ConversacionCeoIa = ConversacionIa & { original: Conversacion };

function aConversacionIa(c: Conversacion): ConversacionCeoIa {
  return {
    id: c.id,
    numero: null,
    titulo: c.titulo?.trim() || "Sin título",
    fijada: c.fijada,
    fecha: c.fechaUltimoMensaje ?? c.fechaCreacion,
    propia: c.esMia,
    compartida: c.visibilidad === "compartida",
    archivada: c.estado === "archivada",
    detalle: c.esMia ? null : `Compartida por ${c.duenoNombre ?? "otro usuario"}`,
    original: c,
  };
}

/** Historial del CEO en el formato del chat global: mías, compartidas conmigo y archivadas. */
function useHistorialCeo() {
  const consulta = useConversaciones();
  const listas = useMemo(() => {
    const visibles = (consulta.data ?? [])
      .filter((c) => !c.esPrueba)
      .filter((c) => c.esMia || c.visibilidad === "compartida")
      .map(aConversacionIa);
    return separarHistorial(visibles);
  }, [consulta.data]);
  return { ...listas, cargando: consulta.isLoading, error: consulta.isError, recargar: consulta.refetch };
}

/**
 * Repite la pregunta de una conversación en una nueva. Si solo tenía el brief, pide el brief de
 * hoy (es lo que se repite).
 */
async function repetirDe(
  c: ConversacionIa,
  chat: ChatCeo,
  o: { modo?: ModoAgente; contextoPantalla?: ContextoPantalla | null },
) {
  try {
    const mensajes = await leerMensajesCeo(c.id);
    const hilo = mensajes.map((m) => ({ rol: m.rol, texto: m.contenido }));
    const pregunta = primeraPregunta(hilo, [MARCA_BRIEF]);
    if (pregunta) return void chat.enviar(pregunta, { ...o, nueva: true });
    if (hilo.some((m) => m.rol === "usuario" && m.texto.trim() === MARCA_BRIEF)) {
      return void chat.pedirBrief({ contextoPantalla: o.contextoPantalla });
    }
    toast("Esa conversación no tiene preguntas para repetir");
  } catch {
    toast.error("No se pudo leer la conversación. Intenta de nuevo.");
  }
}

/**
 * Historial del Agente CEO (drawer y página). `onElegido`: tras abrir o repetir (en el panel,
 * debajo de `lg`, vuelve al chat).
 */
export function HistorialAgenteCeo({
  chat,
  contextoPantalla,
  onElegido,
  className,
}: {
  chat: ChatCeo;
  contextoPantalla?: ContextoPantalla | null;
  onElegido?: () => void;
  className?: string;
}) {
  const historial = useHistorialCeo();
  const actualizar = useActualizarConversacion();
  const [abriendo, setAbriendo] = useState<string | null>(null);

  const conAviso = (exito: string) => ({
    onSuccess: () => toast.success(exito),
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "No se pudo completar la acción"),
  });

  return (
    <ChatIaHistorial<ConversacionCeoIa>
      className={className}
      conversaciones={historial.propias}
      compartidas={historial.compartidas}
      archivadas={historial.archivadas}
      cargando={historial.cargando}
      error={historial.error}
      activa={chat.idConversacion}
      abriendo={abriendo}
      puedePreguntar={!chat.enVuelo}
      onAbrir={(c) => {
        chat.abrirExistente(c.id);
        onElegido?.();
      }}
      onRepetir={async (c) => {
        setAbriendo(c.id);
        await repetirDe(c, chat, { contextoPantalla });
        setAbriendo(null);
        onElegido?.();
      }}
      onRenombrar={(c, titulo) => actualizar.mutate({ id: c.id, titulo }, conAviso("Título actualizado"))}
      onFijar={(c, fijada) => actualizar.mutate({ id: c.id, fijada })}
      onEliminar={(c) =>
        actualizar.mutate(
          { id: c.id, estado: "eliminada" },
          {
            onSuccess: () => {
              toast.success("Conversación eliminada");
              if (chat.idConversacion === c.id) chat.limpiar();
            },
            onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo eliminar"),
          },
        )
      }
      onReintentar={() => void historial.recargar()}
      textoEliminar="dejará de aparecer en tu historial. Es una baja lógica: los mensajes se conservan en la bitácora y se puede recuperar desde la base de datos si hiciera falta."
      accionesExtra={(c) => {
        const compartida = c.original.visibilidad === "compartida";
        const archivada = c.original.estado === "archivada";
        return (
          <>
            <DropdownMenuItem
              className="gap-2 text-[13px]"
              onSelect={() =>
                actualizar.mutate(
                  { id: c.id, visibilidad: compartida ? "privada" : "compartida" },
                  conAviso(compartida ? "Ya no está compartida" : "Compartida con Dirección"),
                )
              }
            >
              <Share2 className="size-4" />
              {compartida ? "Dejar de compartir" : "Compartir con Dirección"}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2 text-[13px]"
              onSelect={() =>
                actualizar.mutate(
                  { id: c.id, estado: archivada ? "activa" : "archivada" },
                  conAviso(archivada ? "Restaurada" : "Archivada"),
                )
              }
            >
              <Archive className="size-4" />
              {archivada ? "Restaurar" : "Archivar"}
            </DropdownMenuItem>
          </>
        );
      }}
    />
  );
}

export function AgenteCeoConversacion({
  contextoPantalla,
  chat,
  soloLectura = false,
}: {
  contextoPantalla?: ContextoPantalla | null;
  chat: ChatCeo;
  /** Una conversación compartida se lee; escribir en ella es de su dueño. */
  soloLectura?: boolean;
}) {
  const { idConversacion, turno, enVuelo, error, pedirBrief, enviar, detener } = chat;
  const { data: guardados = [] } = useMensajes(idConversacion);
  const { data: ocultos } = useMensajesOcultos(idConversacion);
  const ocultar = useOcultarMensaje();
  const historial = useHistorialCeo();
  const [texto, setTexto] = useState("");
  const [modo, setModo] = useState<ModoAgente>("rapido");
  const [verOcultos, setVerOcultos] = useState(false);
  /** La pregunta del turno en curso: se ve en el hilo antes de que la base la devuelva. */
  const [preguntaEnVuelo, setPreguntaEnVuelo] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const cuadroRef = useRef<HTMLTextAreaElement>(null);

  // Dictado: lo transcrito se ACUMULA en el cuadro; el envío siempre es manual.
  const dictado = useDictado((fragmento) =>
    setTexto((t) => (t ? `${t} ${fragmento}`.replace(/\s+/g, " ") : fragmento).trimStart()),
  );

  // Abrir NO llama al modelo ni crea conversación: eso dejó 21 de 25
  // conversaciones vacías. El brief se pide con el chip de la bienvenida.

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turno.texto, turno.estado, guardados.length, enVuelo]);

  // Entrar a una conversación (nueva o existente) deja el cursor donde se escribe.
  useEffect(() => {
    if (!soloLectura) cuadroRef.current?.focus();
  }, [idConversacion, soloLectura]);

  /**
   * Lo guardado manda salvo en el turno que está corriendo: mientras el último
   * mensaje del asistente no se haya persistido, se pinta el texto en vivo.
   */
  const enVivoPendiente = !!turno.texto && !guardados.some((m) => m.id === turno.idMensaje);
  const vacio = guardados.length === 0 && !turno.texto && !enVuelo;

  const mandar = (q: string, o: { nueva?: boolean } = {}) => {
    const t = q.trim();
    if (!t || enVuelo) return;
    if (q === texto) setTexto("");
    setPreguntaEnVuelo(t);
    void enviar(t, { modo, contextoPantalla, nueva: o.nueva });
  };

  const mensajes: MensajeCeo[] = [];
  guardados.forEach((m, i) => {
    const estaOculto = ocultos?.has(m.id) ?? false;
    if (estaOculto && !verOcultos) return;
    // El marcador del brief no es un mensaje de nadie: no se pinta.
    if (m.rol === "usuario" && m.contenido.trim() === MARCA_BRIEF) return;
    const esRespuestaDeBrief =
      m.rol === "asistente" && guardados[i - 1]?.rol === "usuario" && guardados[i - 1]?.contenido.trim() === MARCA_BRIEF;
    mensajes.push({
      id: m.id,
      rol: m.rol,
      texto: m.contenido,
      atenuado: estaOculto,
      brief: esRespuestaDeBrief
        ? new Date(m.fechaCreacion).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
        : null,
      guardado: m,
    });
  });
  const ultimoUsuario = [...guardados].reverse().find((m) => m.rol === "usuario")?.contenido.trim();
  if ((enVuelo || enVivoPendiente) && preguntaEnVuelo && ultimoUsuario !== preguntaEnVuelo) {
    mensajes.push({ id: "pregunta-en-vuelo", rol: "usuario", texto: preguntaEnVuelo });
  }
  if (enVivoPendiente) mensajes.push({ id: "vivo", rol: "asistente", texto: turno.texto, enVivo: true });

  const alternarOculto = (m: MensajeGuardado, estaOculto: boolean) =>
    ocultar.mutate(
      { idMensaje: m.id, mostrar: estaOculto },
      {
        onSuccess: () =>
          toast.success(
            estaOculto ? "De vuelta en el contexto" : "Quitado del contexto: el agente ya no lo tomará en cuenta",
          ),
        onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo"),
      },
    );

  const chipsSugeridas = SUGERIDAS.map((s) => (
    <button
      key={s}
      type="button"
      disabled={soloLectura}
      onClick={() => mandar(s)}
      className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50"
    >
      {s}
    </button>
  ));

  const composer = soloLectura ? (
    <div className="border-t border-border p-3">
      <p className="text-[11px] text-muted-foreground">
        Esta conversación es de otra persona y te la compartieron: puedes leerla, no continuarla.
        Para preguntar lo tuyo, abre una nueva o repite su pregunta.
      </p>
    </div>
  ) : (
    <ChatIaComposer
      inputRef={cuadroRef}
      valor={texto}
      onCambiar={setTexto}
      onEnviar={() => mandar(texto)}
      enviando={enVuelo}
      placeholder="Pregunta sobre servicio, crecimiento o qué construir"
      ariaLabel="Tu pregunta para el Agente CEO"
      arriba={
        <>
          {(["rapido", "profundo"] as ModoAgente[]).map((m) => (
            <Tooltip key={m}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setModo(m)}
                  aria-pressed={modo === m}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors",
                    modo === m
                      ? "border-primary bg-primary/10 font-medium text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {m === "rapido" ? <Zap className="h-3 w-3" /> : <Brain className="h-3 w-3" />}
                  {m === "rapido" ? "Rápido" : "Profundo"}
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                {m === "rapido"
                  ? "Responde en segundos. Para preguntas directas."
                  : "Tarda más y razona mejor; úsalo para análisis y planes."}
              </TooltipContent>
            </Tooltip>
          ))}
          {enVuelo && (
            <Button size="sm" variant="outline" className="ml-auto h-7 gap-1 text-xs" onClick={detener}>
              <Square className="h-3 w-3" /> Detener
            </Button>
          )}
        </>
      }
      accesorio={
        // Dictado: el texto cae en el cuadro y se revisa. Nunca se envía solo: un reconocedor
        // se equivoca con nombres y cifras, y una pregunta mal transcrita da una respuesta
        // correcta a la pregunta equivocada.
        dictado.soportado ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={dictado.grabando ? "Detener dictado" : "Dictar"}
                onClick={dictado.grabando ? dictado.detener : dictado.iniciar}
                className={cn(
                  "mb-1 shrink-0 rounded-full p-1.5 transition-colors",
                  dictado.grabando
                    ? "animate-pulse bg-destructive/15 text-destructive"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Mic className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              {dictado.grabando
                ? "Grabando, toca para detener. Revisa el texto antes de enviar."
                : `Dictar (es-MX). Máximo ${dictado.limiteSegundos / 60} minutos; el texto queda aquí para que lo revises.`}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="mb-1 shrink-0 cursor-default rounded-full p-1.5 text-muted-foreground/40">
                <MicOff className="h-4 w-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              Tu navegador no soporta dictado por voz. Funciona en Chrome y Edge.
            </TooltipContent>
          </Tooltip>
        )
      }
      debajo={
        <>
          {dictado.grabando && dictado.parcial && (
            <p className="mt-1.5 text-[11px] italic text-muted-foreground">{dictado.parcial}</p>
          )}
          {/* Quitar algo del contexto no le borra nada al agente: lo que aprende vive en
              Configuración. Sin esta línea la gente busca la memoria aquí. */}
          <p className="mt-1 px-1 text-[10px] leading-snug text-muted-foreground">
            Quitar un mensaje solo lo saca de <span className="font-medium">esta</span> conversación.
            Lo que el agente recuerda entre conversaciones vive en Configuración › Memoria.
          </p>
        </>
      }
    />
  );

  return (
    <ChatIaCuerpo composer={composer}>
      {vacio && !idConversacion ? (
        <ChatIaBienvenida<ConversacionCeoIa>
          titulo="Agente CEO"
          descripcion="Servicio al cliente, crecimiento y qué construir, sobre los datos del portal. La conversación se guarda cuando envíes la primera pregunta."
          sugerencias={SUGERIDAS}
          continuar={historial.propias[0] ?? null}
          onContinuar={(c) => chat.abrirExistente(c.id)}
          recientes={historial.propias.slice(1, 4)}
          deshabilitado={enVuelo}
          onPreguntar={(s) => mandar(s)}
          onRepetir={(c) => void repetirDe(c, chat, { modo, contextoPantalla })}
          extra={
            // El brief se pide: no se dispara al abrir. Se mantiene la reutilización de 2 horas
            // del lado del servidor.
            <button
              type="button"
              onClick={() => void pedirBrief({ contextoPantalla })}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-[12px] font-medium text-primary transition-colors hover:bg-primary/10"
            >
              <FileText className="h-3.5 w-3.5" /> Dame el brief de hoy
            </button>
          }
        />
      ) : (
        <ChatIaMensajes<MensajeCeo>
          mensajes={mensajes}
          deshabilitado={enVuelo}
          escribiendo={enVuelo && (!turno.texto || !!turno.estado)}
          textoEscribiendo={turno.estado || (turno.texto ? null : "Pensando")}
          onRepetir={(t) => mandar(t, { nueva: true })}
          onEditar={
            soloLectura
              ? undefined
              : (t) => {
                  setTexto(t);
                  window.setTimeout(() => cuadroRef.current?.focus(), 0);
                }
          }
          renderRespuesta={(m) => (
            <>
              {m.brief && (
                <p className="mb-1.5 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                  <FileText className="h-3 w-3" />
                  Brief · {m.brief}
                </p>
              )}
              <Parrafos texto={m.texto} k={m.id} />
              {m.enVivo && enVuelo && (
                <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-primary align-middle" />
              )}
              {m.enVivo && turno.propuestas.map((p) => <TarjetaPropuesta key={p.id} propuesta={p} />)}
            </>
          )}
          renderPie={(m) => {
            if (m.enVivo) {
              return enVuelo ? null : (
                <PieRespuesta idMensaje={turno.idMensaje} texto={turno.texto} herramientas={turno.herramientas} />
              );
            }
            const g = m.guardado;
            if (!g) return null;
            return (
              <PieRespuesta
                idMensaje={g.id}
                texto={g.contenido}
                herramientas={g.herramientas}
                oculto={!!m.atenuado}
                onOcultar={soloLectura ? undefined : () => alternarOculto(g, !!m.atenuado)}
              />
            );
          }}
        />
      )}

      {vacio && idConversacion && guardados.length === 0 && <EsqueletoHilo />}

      {error && (
        <ChatIaAviso
          mensaje={error.mensaje}
          onReintentar={
            error.codigo !== "sin_permiso" && !soloLectura
              ? () => void enviar("Reintenta lo último", { modo, contextoPantalla })
              : undefined
          }
          reintentando={enVuelo}
        />
      )}

      {/* En una conversación recién empezada, las sugeridas siguen a la mano. */}
      {!vacio && idConversacion && guardados.length > 0 && guardados.length <= 2 && !enVuelo && !soloLectura && (
        <div className="mt-4 flex flex-wrap gap-1.5">{chipsSugeridas}</div>
      )}

      {(ocultos?.size ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => setVerOcultos((v) => !v)}
          className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {verOcultos ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          {verOcultos
            ? "Ocultar los quitados del contexto"
            : `Ver ${ocultos!.size} mensaje${ocultos!.size === 1 ? "" : "s"} quitado${ocultos!.size === 1 ? "" : "s"} del contexto`}
        </button>
      )}
      <div ref={finRef} />
    </ChatIaCuerpo>
  );
}

export function EncabezadoAgente({ subtitulo }: { subtitulo?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary/10 text-primary">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">Agente CEO</p>
        {subtitulo && <p className="truncate text-[11px] text-muted-foreground">{subtitulo}</p>}
      </div>
    </div>
  );
}
